import React from 'react';
import { createPortal } from 'react-dom';
import { useLeadById } from '../hooks/useLeadById';
import { LeadDetailsDialog } from './LeadDetailsDialog';
import { PopulatedLead } from '../types';

/**
 * Props do LeadQuickViewDialog
 */
export interface LeadQuickViewDialogProps {
  /** ID do lead a ser exibido (null = fechado) */
  leadId: string | null;
  /** Se o dialog está aberto */
  isOpen: boolean;
  /** Callback para fechar o dialog */
  onClose: () => void;
}

/**
 * Wrapper reutilizável que recebe apenas um leadId,
 * busca os dados com useLeadById e renderiza LeadDetailsDialog.
 *
 * Usado para abrir detalhes do lead direto de qualquer lugar
 * sem precisar navegar para a página do CRM.
 *
 * @example
 * ```tsx
 * <LeadQuickViewDialog
 *   leadId={selectedLeadId}
 *   isOpen={!!selectedLeadId}
 *   onClose={() => setSelectedLeadId(null)}
 * />
 * ```
 */
export const LeadQuickViewDialog: React.FC<LeadQuickViewDialogProps> = ({
  leadId,
  isOpen,
  onClose
}) => {
  const { data: lead, isLoading } = useLeadById(leadId || undefined);

  if (!isOpen || !leadId) return null;

  // Loading state: overlay escuro com spinner (always-light)
  if (isLoading || !lead) {
    return createPortal(
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={onClose}
      >
        <div
          className="flex flex-col items-center gap-3 p-8 rounded-lg"
          style={{ backgroundColor: '#ffffff' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: '#8b5cf6', borderTopColor: 'transparent' }}
          />
          <span className="text-sm font-medium" style={{ color: '#64748b' }}>
            Carregando lead...
          </span>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <LeadDetailsDialog
      isOpen={isOpen}
      onClose={onClose}
      lead={lead as PopulatedLead}
    />
  );
};

LeadQuickViewDialog.displayName = 'LeadQuickViewDialog';
